/**
 * A transaction's fees (IP-203; design
 * docs/superpowers/specs/2026-10-19-apply-fees-checkbox-design.md, sections
 * 3 to 5). Pure: rows in, decisions out - no I/O, no clock.
 *
 * The fee percentages are set at FUND level only (fund_config, by four eyes,
 * effective from a date). Per transaction leg, the reviewer only decides
 * whether the fund's fee applies at all - the Apply fees checkbox, kept as
 * "order".fees_waived (unticked = waived). The fee itself lives in the
 * PRICE: a NAV run strikes a selling price with entry fee and a buying price
 * with exit fee from the rates in force on its valuation date, and keeps
 * those rates beside them (nav_run / price entry_fee_rate, exit_fee_rate).
 *
 * feeTermsOf is the ONE place that decides, for one leg at one price,
 *
 *   applicable   does the fund charge this leg's kind of fee? A
 *                subscription or switch-in pays the (target) fund's
 *                front-end fee, a redemption or switch-out its exit fee; a
 *                transfer never pays one. Read from the dealing price when
 *                there is one (a price with entry fee above the selling price,
 *                a price with exit fee below the buying price), else from the
 *                fund_config in force on the dealing date - "provisional";
 *   applied      applicable and not waived: deal at the price WITH the fee;
 *   rate         the % the leg is charged: the price's rate (else the
 *                config's) when applied, 0 when not;
 *   reason       why the checkbox is off and disabled: NO_ENTRY_FEE,
 *                NO_EXIT_FEE or TRANSFER (null when it may be changed).
 *
 * Every caller asks it: repricing (repricing/quote.js, reprice.js) to choose
 * the price and capture the rate, the approval's G-3 (gates.js) to hold an
 * amount to one unit, allocation (register/post.js) to strike the figures,
 * the review (review.js) to refuse a waiver that means nothing, and the
 * reads (orders/reads.js) to draw the checkbox and its tooltip. So the
 * figure shown at review is the figure posted.
 *
 * An exit fee is still paid only on the units held less than the holding
 * period (IP-98; the owner's answer of 19 Oct 2026): `applied` turns the
 * young-lot rule on, and register/allocate.js allocateOut applies it lot by
 * lot. holdingMonths is returned for the words beside the %.
 *
 * Retired here by IP-203: IP-139's per-order entry-fee rate
 * (entry_fee_rate_override, entryFeeRateOf), and IP-137's fee taken off the
 * amount (suggestedFee) - a subscription is now dealt at the selling price
 * with entry fee instead (allocate.js allocateSub).
 *
 * IP-204 (design docs/superpowers/specs/2026-10-20-apply-fees-default-
 * conditions-design.md): the box's FIRST state. "order".fees_waived is NULL
 * until somebody chooses - the review writes the value it showed - and while
 * it is NULL the fund decides, by feeDefaultOf below:
 *
 *   no condition     (fund_config entry_fee_default_above /
 *                    exit_fee_default_above NULL) - ticked, as before IP-204;
 *   a condition X    ticked only when the leg's value is MORE THAN X. Equal
 *                    or below: unticked. The value is the leg's amount, or -
 *                    for a leg by units or a full holding - its units valued
 *                    at its price (section 3.1 of the design): the plain
 *                    buying price for money out (never the price with the exit
 *                    fee, which would make the test depend on the fee it
 *                    decides), the selling price with entry fee for money in
 *                    (what the investor pays). A switch-in's value is the LKR
 *                    its switch-out releases.
 *
 * The price that values a leg is its dealing date's when published, else the
 * one the caller hands as valuePrice (the price repricing used, or the fund's
 * latest published) - provisional. No price at all: ticked, provisional - an
 * unknown value never waives a fee by itself.
 */
import { cmp, isDecimal, mul, toFixed } from '../decimal.js';

/** The kind of fee a leg can pay, by order type. */
export const FEE_KIND = Object.freeze({ ENTRY: 'ENTRY', EXIT: 'EXIT' });

/** Why a leg's Apply fees checkbox is off and cannot be changed. */
export const FEE_REASON = Object.freeze({
  NO_ENTRY_FEE: 'NO_ENTRY_FEE', // the (target) fund charges no front-end fee on the dealing date
  NO_EXIT_FEE: 'NO_EXIT_FEE',   // the fund charges no exit fee on the dealing date
  TRANSFER: 'TRANSFER',         // a transfer moves units at cost: never a fee
});

const ENTRY_TYPES = new Set(['SUB', 'SWI_IN']);
const EXIT_TYPES = new Set(['RED', 'SWI_OUT']);
const TRANSFER_TYPES = new Set(['TRF_OUT', 'TRF_IN']);

const isSet = (value) => value !== null && value !== undefined;
/** A rate as a decimal string, or null for none (a driver may hand numeric as a number). */
const rateText = (value) => (isSet(value) && isDecimal(String(value)) ? String(value) : null);
const above = (a, b) => isSet(a) && isSet(b) && cmp(String(a), String(b)) > 0;

/**
 * The fee kind a leg's type can pay, or null: a transfer, or a row with no order (an upload or a conversion).
 *
 * @param {string|null|undefined} orderType
 * @returns {'ENTRY'|'EXIT'|null}
 */
export function feeKindOf(orderType) {
  if (ENTRY_TYPES.has(orderType)) return FEE_KIND.ENTRY;
  if (EXIT_TYPES.has(orderType)) return FEE_KIND.EXIT;
  return null;
}

/**
 * Whether, and at what rate, the fund charges a fee of this kind - from the
 * dealing price when there is one, else from the dealing date's fund_config.
 *
 * A price decides by its own figures, so a price struck before 0038 (no rate
 * column filled) still deals as it was struck: a selling price with entry fee
 * above the selling price IS a front-end fee. Its rate then comes from the
 * config. A config decides by its rates: a front-end rate above 0; an exit
 * fee that is a percentage above 0, on a fund LIV flags as charging one
 * (IP-99: off, nothing is charged whatever the rule names).
 */
function chargedBy(kind, price, config) {
  if (kind === FEE_KIND.ENTRY) {
    const configRate = rateText(config?.entry_fee_rate);
    if (price) {
      const charged = above(price.selling_price_with_entry_fee, price.selling_price);
      return { charged, rate: charged ? rateText(price.entry_fee_rate) ?? configRate : null };
    }
    const charged = isSet(configRate) && cmp(configRate, '0') > 0;
    return { charged, rate: charged ? configRate : null };
  }
  const configRate = config?.exit_fee_basis === 'PERCENT' ? rateText(config?.exit_fee_pct) : null;
  if (price) {
    const bwef = price.buying_price_with_exit_fee;
    const charged = isSet(bwef) && above(price.buying_price, bwef);
    return { charged, rate: charged ? rateText(price.exit_fee_rate) ?? configRate : null };
  }
  const charged = config?.is_exit_fee_required !== false && isSet(configRate) && cmp(configRate, '0') > 0;
  return { charged, rate: charged ? configRate : null };
}

/** A money figure at 2 dp, or null for none (a driver may hand numeric as a number). */
const moneyText = (value) => (isSet(value) && isDecimal(String(value)) ? toFixed(String(value), 2) : null);

/**
 * IP-204: the value the fund's condition is compared with, and the price it
 * was valued at - or null when the leg's figures cannot say yet.
 *
 * The leg's own amount when the investor named one: LIV sends it on an
 * AMOUNT-based leg. Otherwise its units valued at `price` (section 3.1 of the
 * design): money in at the selling price with entry fee (the money the
 * investor pays; the selling price if the price struck none), money out at the
 * plain buying price. A full holding takes the units reserved when it was
 * taken (reserved_units), else the units it names. A switch-in (DERIVED) has
 * no figure of its own: its value is the LKR its switch-out releases, which
 * the caller hands as derivedAmount.
 */
function legValueOf(kind, order, price, derivedAmount) {
  if (order?.basis === 'DERIVED') {
    const amount = moneyText(derivedAmount);
    return amount ? { amount, valuedAt: null } : null;
  }
  // A full holding: its units, as a read hands them (full_units, approval/sql.js FEE_DEFAULT_COLUMNS) or the row has them.
  const fullHolding = isSet(order?.full_units) || order?.is_full_holding === true;
  if (!fullHolding && order?.basis === 'AMOUNT') {
    const amount = moneyText(order.amount);
    return amount ? { amount, valuedAt: null } : null;
  }
  const units = rateText(fullHolding ? (order.full_units ?? order.reserved_units ?? order.units) : order?.units);
  if (!units || !price) return null;
  // A valuation price read as one figure (approval/sql.js VALUE_PRICE_COLUMNS) is already the right side.
  const unitPrice = rateText(price.unit_price)
    ?? (kind === FEE_KIND.ENTRY ? rateText(price.selling_price_with_entry_fee) ?? rateText(price.selling_price) : rateText(price.buying_price));
  if (!unitPrice) return null;
  return { amount: toFixed(mul(units, unitPrice), 2), valuedAt: toFixed(unitPrice, 4), units: toFixed(units, 4) };
}

/**
 * IP-204: the fund's default for a leg's Apply fees box - what feeTermsOf
 * uses while "order".fees_waived is NULL (see the header).
 *
 * @param {object} args
 * @param {'ENTRY'|'EXIT'} args.kind the leg's fee kind (feeKindOf)
 * @param {object} args.order the leg: basis, is_full_holding, amount, units, reserved_units
 * @param {object|null} [args.price] the leg's dealing date price, or null
 * @param {object|null} [args.valuePrice] the price to value units at when there is no dealing price
 * @param {object|null} [args.config] the dealing date's fund_config (entry_fee_default_above, exit_fee_default_above)
 * @param {string|null} [args.derivedAmount] a DERIVED switch-in's LKR: its switch-out's net
 * @returns {{ applies: boolean, threshold: string|null, amount: string|null, units: string|null,
 *   valuedAt: string|null, priceDate: string|null, provisional: boolean }}
 *   applies: the box starts ticked; threshold: the condition's amount (null: no condition); amount: the value
 *   compared (null: unknown, so ticked); valuedAt / units / priceDate: how a leg by units was valued
 */
export function feeDefaultOf({ kind, order, price = null, valuePrice = null, config = null, derivedAmount = null }) {
  // The condition that governs this kind of fee; a narrow read hands the one that applies as fee_default_above.
  const threshold = moneyText(config?.fee_default_above
    ?? (kind === FEE_KIND.ENTRY ? config?.entry_fee_default_above : config?.exit_fee_default_above));
  if (!threshold) {
    // No condition switched on: ticked, as every leg was before IP-204.
    return { applies: true, threshold: null, amount: null, units: null, valuedAt: null, priceDate: null, provisional: false };
  }
  const valuing = price ?? valuePrice ?? null;
  const value = legValueOf(kind, order, valuing, derivedAmount);
  if (!value) {
    // The value cannot be known yet (no price to value the units at): ticked, until it can.
    return { applies: true, threshold, amount: null, units: null, valuedAt: null, priceDate: null, provisional: true };
  }
  return {
    // MORE THAN the amount, never "or equal": the owner's rule (IP-204).
    applies: cmp(value.amount, threshold) > 0,
    threshold,
    amount: value.amount,
    units: value.units ?? null,
    valuedAt: value.valuedAt,
    priceDate: value.valuedAt ? valuing?.dealing_date ?? null : null,
    // Final only when the units were valued at the dealing date's own price, or no price was needed.
    provisional: Boolean(value.valuedAt) && !price,
  };
}

/**
 * The fee terms of one leg at one price (see the header).
 *
 * @param {object} args
 * @param {{ order_type: string, fees_waived?: boolean|null }} args.order the leg (IP-204: with basis,
 *   is_full_holding, amount, units and reserved_units, which value it for the fund's default)
 * @param {object|null} [args.price] the leg's DEALING DATE price row (selling_price, selling_price_with_entry_fee,
 *   buying_price, buying_price_with_exit_fee, entry_fee_rate, exit_fee_rate). Null: not priced yet - the answer
 *   is provisional, from the config
 * @param {object|null} [args.config] the fund_config in force on the dealing date (entry_fee_rate, exit_fee_basis,
 *   exit_fee_pct, exit_fee_holding_months, IP-204's entry_fee_default_above and exit_fee_default_above) with the
 *   fund's is_exit_fee_required
 * @param {object|null} [args.valuePrice] IP-204: the price to value a leg by units at when `price` is null
 * @param {string|null} [args.derivedAmount] IP-204: a DERIVED switch-in's LKR (its switch-out's net)
 * @returns {{ kind: 'ENTRY'|'EXIT'|null, applicable: boolean, waived: boolean, applied: boolean, rate: string|null,
 *   fundRate: string|null, provisional: boolean, reason: string|null, holdingMonths: number|null,
 *   byDefault: boolean, default: object|null }}
 *   rate: the % charged ('0' when not applied, null on a transfer); fundRate: the fund's % whether applied or not;
 *   byDefault: nobody has chosen, so `waived` is the fund's default; default: feeDefaultOf's answer when a
 *   condition is switched on and the fee is applicable, else null
 */
export function feeTermsOf({ order, price = null, config = null, valuePrice = null, derivedAmount = null }) {
  const kind = feeKindOf(order?.order_type);
  if (!kind) {
    // A transfer (or a row with no order): no fee, and nothing to tick.
    return {
      kind: null, applicable: false, waived: false, applied: false, rate: null, fundRate: null, provisional: false,
      reason: TRANSFER_TYPES.has(order?.order_type) ? FEE_REASON.TRANSFER : null, holdingMonths: null,
      byDefault: false, default: null,
    };
  }
  const { charged, rate } = chargedBy(kind, price, config);
  // IP-204: a person's choice when there is one (true / false); NULL is nobody's yet, and the fund decides.
  const chosen = order?.fees_waived;
  const byDefault = chosen !== true && chosen !== false;
  const fallback = byDefault && charged ? feeDefaultOf({ kind, order, price, valuePrice, config, derivedAmount }) : null;
  const waived = byDefault ? (fallback ? !fallback.applies : false) : chosen === true;
  const applied = charged && !waived;
  const months = config?.exit_fee_holding_months;
  return {
    kind,
    applicable: charged,
    waived,
    applied,
    // What the leg is charged: the fund's % when the fee is applied, nothing otherwise.
    rate: applied ? rate : '0',
    fundRate: rate,
    provisional: !price,
    reason: charged ? null : kind === FEE_KIND.ENTRY ? FEE_REASON.NO_ENTRY_FEE : FEE_REASON.NO_EXIT_FEE,
    holdingMonths: kind === FEE_KIND.EXIT && charged && isSet(months) ? Number(months) : null,
    byDefault,
    // Only when a condition is switched on: what the Console words beside the box, and the review records.
    default: fallback && fallback.threshold ? fallback : null,
  };
}

/**
 * feeTermsOf for a row that selects the order (order_type, fees_waived), the
 * dealing date's fund_config columns with is_exit_fee_required, and the
 * dealing price as approval/sql.js DEALING_PRICE_COLUMNS' dp_* columns - the
 * review's read and the order reads both do.
 *
 * IP-204: with VALUE_PRICE_COLUMNS' vp_* columns too - the unit price, from
 * the fund's latest published price on or before the dealing date, that values a leg by units
 * while its own date has no price - and linked_net, a switch-in's switch-out
 * net once that is repriced. A row without them still reads: a leg by units
 * then has no value before its dealing price, and starts ticked.
 *
 * @param {object} row
 * @returns {object} feeTermsOf's answer
 */
export function feeTermsOfRow(row) {
  const price = isSet(row?.dp_price_id) ? {
    dealing_date: row.dealing_date ?? null,
    selling_price: row.dp_selling_price,
    selling_price_with_entry_fee: row.dp_selling_price_with_entry_fee,
    buying_price: row.dp_buying_price,
    buying_price_with_exit_fee: row.dp_buying_price_with_exit_fee,
    entry_fee_rate: row.dp_entry_fee_rate,
    exit_fee_rate: row.dp_exit_fee_rate,
  } : null;
  const valuePrice = isSet(row?.vp_unit_price) ? { dealing_date: row.vp_dealing_date, unit_price: row.vp_unit_price } : null;
  return feeTermsOf({ order: row, price, config: row, valuePrice, derivedAmount: row?.linked_net ?? null });
}

/**
 * The fee % of a POSTED register row, as every screen shows it beside the
 * fee. A row posted since 0038 carries what it was charged
 * (unit_transaction.fees_applied, fee_rate) and that is the answer. An older
 * row is derived as before 0038: the fund's rules on the dealing date, and
 * (IP-139) the order's own entry rate when its review set one.
 *
 * @param {object} row a register read (order_type, fees_applied, txn_fee_rate, and the dealing date's fund_config
 *   columns: entry_fee_rate, entry_fee_rate_override, exit_fee_basis, exit_fee_pct, exit_fee_holding_months,
 *   is_exit_fee_required)
 * @returns {{ kind: 'ENTRY'|'EXIT', rate: string|null, fundRate: string|null, applied: boolean, overridden: boolean,
 *   provisional: false, holdingMonths: number|null } | null} null: no fee % applies (a transfer, an upload);
 *   overridden: an older subscription charged its own rate set at review (IP-139)
 */
export function postedFeeRateOf(row) {
  const kind = feeKindOf(row?.order_type);
  if (!kind) return null;
  const months = kind === FEE_KIND.EXIT && isSet(row.exit_fee_holding_months) ? Number(row.exit_fee_holding_months) : null;
  if (isSet(row.fees_applied)) {
    // Posted since 0038: exactly what allocation charged.
    const fundRate = kind === FEE_KIND.ENTRY ? rateText(row.entry_fee_rate) : rateText(row.exit_fee_pct);
    return {
      kind, rate: rateText(row.txn_fee_rate) ?? '0', fundRate, applied: row.fees_applied === true, overridden: false, provisional: false,
      holdingMonths: row.fees_applied === true ? months : null,
    };
  }
  // Posted before 0038: the rules the posting was charged from.
  if (kind === FEE_KIND.ENTRY) {
    // A switch-in paid no fee before 0038.
    if (row.order_type !== 'SUB') return null;
    const fundRate = rateText(row.entry_fee_rate);
    const own = rateText(row.entry_fee_rate_override);
    const rate = own ?? fundRate;
    return { kind, rate, fundRate, applied: isSet(rate) && cmp(rate, '0') > 0, overridden: isSet(own), provisional: false, holdingMonths: null };
  }
  if (row.is_exit_fee_required === false) return null;
  if (row.exit_fee_basis !== 'PERCENT' || !isSet(row.exit_fee_pct)) return null;
  const rate = rateText(row.exit_fee_pct);
  return { kind, rate, fundRate: rate, applied: true, overridden: false, provisional: false, holdingMonths: months };
}
